/**
 * The app's own right-click menu, in the look of the menu bar's: the commands for whatever
 * was clicked. One is open at a time. A click outside it, Escape, Tab, a scroll, or leaving
 * the window closes it, and the cursor goes back to where it was.
 */
export interface MenuCommand {
  label: string;
  /** The key that does the same thing where the menu was opened, shown on the right. */
  shortcut?: string;
  run?: () => void;
  /** Shown greyed out: a command that can't be used here right now. */
  disabled?: boolean;
  danger?: boolean;
  /** Set in bold, as a spelling suggestion is. */
  strong?: boolean;
  /** Commands one level down, opened by pointing at this one or with →. */
  submenu?: readonly MenuEntry[];
}
/** A command, or "-" for a line between groups. Anything falsy is left out, so a list can say `cond && {…}`. */
export type MenuEntry = MenuCommand | "-" | false | null | undefined;

export interface MenuPlacement {
  /** Where the menu's corner goes, in viewport pixels. It opens the other way where it would run off screen. */
  x: number;
  y: number;
  /** A heading naming what the menu acts on. */
  title?: string;
  /** Opened from the keyboard, so its first command is selected at once. */
  keyboard?: boolean;
  /** Puts the cursor back where it was when the menu closes. */
  returnFocus?: () => void;
  onClose?: () => void;
}

const SUBMENU_OPEN_DELAY = 120;
// Long enough to cross the commands below on the way into a submenu.
const SUBMENU_CLOSE_DELAY = 250;
const MARGIN = 4;

interface OpenMenu {
  root: HTMLElement;
  sub: { menu: HTMLElement; item: HTMLButtonElement } | null;
  placement: MenuPlacement;
  at: { x: number; y: number };
  timer?: ReturnType<typeof setTimeout>;
  stop: () => void;
}
let open: OpenMenu | null = null;
const commands = new WeakMap<HTMLButtonElement, MenuCommand>();

export const contextMenuOpen = (): boolean => open !== null;

/** Whether `node` is in the open menu or its submenu. */
export const inContextMenu = (node: Node | null): boolean =>
  !!open && !!node && (open.root.contains(node) || !!open.sub?.menu.contains(node));

/** The entries left once falsy ones are dropped and no line starts, ends, or doubles a group. */
export function menuEntries(entries: readonly MenuEntry[]): (MenuCommand | "-")[] {
  const kept: (MenuCommand | "-")[] = [];
  for (const entry of entries) {
    if (!entry) continue;
    if (entry === "-" && (!kept.length || kept.at(-1) === "-")) continue;
    kept.push(entry);
  }
  if (kept.at(-1) === "-") kept.pop();
  return kept;
}

export function closeContextMenu(): void {
  const menu = open;
  if (!menu) return;
  open = null;
  clearTimeout(menu.timer);
  menu.stop();
  // The cursor goes back unless something outside the menu has already taken it.
  const focused = document.activeElement;
  if (!focused || focused === document.body || menu.root.contains(focused) || menu.sub?.menu.contains(focused))
    menu.placement.returnFocus?.();
  menu.root.remove();
  menu.sub?.menu.remove();
  menu.placement.onClose?.();
}

export function showContextMenu(
  entries: readonly MenuEntry[],
  placement: MenuPlacement,
  host: HTMLElement = document.body,
): void {
  closeContextMenu();
  const list = menuEntries(entries);
  if (!list.some((entry) => entry !== "-")) return;
  const root = buildMenu(list, placement.title ?? "Menu", placement.title);
  host.append(root);
  place(root, placement.x, placement.y);
  const outside = (event: Event) => {
    if (!inContextMenu(event.target as Node | null)) closeContextMenu();
  };
  const listeners: [EventTarget, string, EventListener, boolean][] = [
    [document, "pointerdown", outside, true],
    [document, "focusin", outside, true],
    [document, "scroll", outside, true],
    [window, "blur", closeContextMenu, false],
    [window, "resize", closeContextMenu, false],
  ];
  for (const [target, type, listener, capture] of listeners) target.addEventListener(type, listener, capture);
  open = {
    root,
    sub: null,
    placement,
    at: { x: placement.x, y: placement.y },
    stop: () => {
      for (const [target, type, listener, capture] of listeners) target.removeEventListener(type, listener, capture);
    },
  };
  // From the keyboard the first command is selected; from a click nothing is until the pointer moves.
  if (placement.keyboard) items(root)[0]?.focus({ preventScroll: true });
  else root.focus({ preventScroll: true });
}

function buildMenu(entries: readonly (MenuCommand | "-")[], label: string, title?: string): HTMLElement {
  const menu = document.createElement("div");
  menu.className = "context-menu";
  menu.setAttribute("role", "menu");
  menu.setAttribute("aria-label", label);
  menu.tabIndex = -1;
  if (title) {
    const heading = document.createElement("div");
    heading.className = "context-menu-title";
    heading.setAttribute("aria-hidden", "true");
    heading.textContent = title;
    menu.append(heading);
  }
  for (const entry of entries) {
    if (entry === "-") {
      const line = document.createElement("div");
      line.className = "context-menu-separator";
      line.setAttribute("role", "separator");
      menu.append(line);
      continue;
    }
    const item = document.createElement("button");
    item.type = "button";
    item.className = ["context-menu-item", entry.danger && "danger", entry.strong && "strong"].filter(Boolean).join(" ");
    item.setAttribute("role", "menuitem");
    item.tabIndex = -1;
    item.disabled = !!entry.disabled;
    const name = document.createElement("span");
    name.className = "context-menu-label";
    name.textContent = entry.label;
    item.append(name);
    if (entry.submenu) {
      item.setAttribute("aria-haspopup", "menu");
      item.setAttribute("aria-expanded", "false");
    }
    if (entry.shortcut || entry.submenu) {
      const key = document.createElement("span");
      key.className = entry.submenu ? "context-menu-key context-menu-arrow" : "context-menu-key";
      key.setAttribute("aria-hidden", "true");
      key.textContent = entry.submenu ? "►" : entry.shortcut!;
      item.append(key);
    }
    commands.set(item, entry);
    menu.append(item);
  }
  menu.addEventListener("keydown", (event) => keydown(event, menu));
  menu.addEventListener("click", (event) => {
    const item = itemAt(event.target);
    if (item) choose(item, false);
  });
  menu.addEventListener("pointermove", (event) => hover(itemAt(event.target), menu));
  menu.addEventListener("pointerleave", () => {
    const sub = open?.sub;
    // Pointing away leaves nothing selected, except the command whose submenu is open.
    if (open && menu.contains(document.activeElement) && document.activeElement !== sub?.item)
      menu.focus({ preventScroll: true });
  });
  // Pressing the right button, moving to a command, and letting go chooses it, as on a Mac;
  // so does right-clicking one. Where the browser's menu event follows the release, the
  // command waits for it, so the event still lands on this menu rather than the page.
  menu.addEventListener("pointerup", (event) => {
    const item = itemAt(event.target);
    if (event.button !== 2 || !item || !open) return;
    if (Math.hypot(event.clientX - open.at.x, event.clientY - open.at.y) <= MARGIN) return;
    setTimeout(() => {
      if (inContextMenu(item)) choose(item, false);
    });
  });
  menu.addEventListener("contextmenu", (event) => event.preventDefault());
  return menu;
}

const items = (menu: HTMLElement) =>
  [...menu.querySelectorAll<HTMLButtonElement>(".context-menu-item")].filter((item) => !item.disabled);
const itemAt = (target: EventTarget | null) =>
  target instanceof Element ? target.closest<HTMLButtonElement>(".context-menu-item:not(:disabled)") : null;

function choose(item: HTMLButtonElement, keyboard: boolean) {
  const command = commands.get(item);
  if (!command) return;
  if (command.submenu) {
    openSubmenu(item, keyboard);
    if (!keyboard) item.focus({ preventScroll: true });
    return;
  }
  closeContextMenu();
  command.run?.();
}

function hover(item: HTMLButtonElement | null, menu: HTMLElement) {
  if (!open) return;
  if (menu !== open.root) {
    // Into the submenu: it stays open.
    clearTimeout(open.timer);
    if (item && document.activeElement !== item) item.focus({ preventScroll: true });
    return;
  }
  if (!item) return;
  if (document.activeElement !== item) item.focus({ preventScroll: true });
  clearTimeout(open.timer);
  const sub = open.sub;
  if (commands.get(item)?.submenu) {
    if (sub?.item !== item) open.timer = setTimeout(() => openSubmenu(item, false), SUBMENU_OPEN_DELAY);
  } else if (sub) open.timer = setTimeout(() => closeSubmenu(false), SUBMENU_CLOSE_DELAY);
}

function openSubmenu(item: HTMLButtonElement, focusFirst: boolean) {
  const command = commands.get(item);
  if (!open || !command?.submenu) return;
  clearTimeout(open.timer);
  if (open.sub?.item !== item) {
    closeSubmenu(false);
    const menu = buildMenu(menuEntries(command.submenu), command.label);
    menu.classList.add("context-submenu");
    open.root.after(menu);
    const row = item.getBoundingClientRect();
    const parent = open.root.getBoundingClientRect();
    const padding = parseFloat(getComputedStyle(menu).paddingTop) || 0;
    // Beside the command, its first row level with it; on the left where the right is too close.
    place(menu, row.right - 2, row.top - padding - 1, parent.left + 2);
    item.setAttribute("aria-expanded", "true");
    open.sub = { menu, item };
  }
  if (focusFirst) items(open.sub!.menu)[0]?.focus({ preventScroll: true });
}

function closeSubmenu(focusItem: boolean) {
  const sub = open?.sub;
  if (!open || !sub) return;
  open.sub = null;
  sub.item.setAttribute("aria-expanded", "false");
  if (focusItem || sub.menu.contains(document.activeElement)) sub.item.focus({ preventScroll: true });
  sub.menu.remove();
}

function keydown(event: KeyboardEvent, menu: HTMLElement) {
  if (!open) return;
  const list = items(menu);
  const at = list.indexOf(document.activeElement as HTMLButtonElement);
  const current = list[at];
  const submenu = menu !== open.root;
  const select = (index: number) => list[(index + list.length) % list.length]?.focus({ preventScroll: true });
  switch (event.key) {
    case "ArrowDown": select(at + 1); break;
    case "ArrowUp": select(at < 0 ? -1 : at - 1); break;
    case "Home": select(0); break;
    case "End": select(-1); break;
    case "ArrowRight":
      if (!current || !commands.get(current)?.submenu) return;
      openSubmenu(current, true);
      break;
    case "ArrowLeft":
      if (!submenu) return;
      closeSubmenu(true);
      break;
    case "Enter":
    case " ":
      if (current) choose(current, true);
      break;
    case "Escape":
      if (submenu) closeSubmenu(true);
      else closeContextMenu();
      break;
    case "Tab": closeContextMenu(); break;
    default: {
      // A letter goes to the next command that starts with it.
      if (event.key.length !== 1 || event.metaKey || event.ctrlKey || event.altKey) return;
      const letter = event.key.toLocaleLowerCase();
      const next = [...list.slice(at + 1), ...list.slice(0, at + 1)]
        .find((item) => commands.get(item)?.label.toLocaleLowerCase().startsWith(letter));
      if (!next) return;
      next.focus({ preventScroll: true });
    }
  }
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Puts a menu's corner at (x, y), or its right edge at `flipX` (else at x) and its foot at
 * y where it would run off the right or the bottom, and inside the window either way.
 */
function place(menu: HTMLElement, x: number, y: number, flipX = x) {
  menu.style.left = "0px";
  menu.style.top = "0px";
  const { width, height } = menu.getBoundingClientRect();
  const right = window.innerWidth - MARGIN,
    bottom = window.innerHeight - MARGIN;
  const left = x + width > right ? flipX - width : x;
  const top = y + height > bottom ? y - height : y;
  menu.style.left = `${Math.max(MARGIN, Math.min(left, right - width))}px`;
  menu.style.top = `${Math.max(MARGIN, Math.min(top, bottom - height))}px`;
}
